import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { CelebrationsWidgetStore } from './celebrations-widget.store';
import { UserWidgetSetting } from '@libs/types/src/services/types/service-settings-user-widgets.type';
import { folderCelebrationsFrag } from './celebrations-widget.query';
import { FolderGridItemObject } from '@it2go/types';
import { FolderStatusEnum } from '../../../../../../../../../types/src/services/enum/folder-status.enum';
import { dateifyOrNow } from '../../../../../shared/helpers/date.helper';
import { formatDate, NgClass } from '@angular/common';
import { TableModule } from '@openng/optimus-ui/table';
import {
    clientChipField,
    dayWithRelationsField,
    serviceField,
} from '../../../../../shared/components/kit/table/table.fields';
import { TranslatePipe } from '@ngx-translate/core';
import { NameDayObject } from '@libs/types/src/list/object-type/name-day.object';
import { UpcomingItem } from './celebrations-widget.types';
import { TableComponent } from '../../../../../shared/components/kit/table/table.component';
import { PrimeTemplate } from '@openng/optimus-ui/api';
import { EmptyStateComponent } from '../../../../../shared/components/empty-state/empty-state.component';

@Component({
    selector: 'app-celebrations-widget',
    templateUrl: './celebrations-widget.component.html',
    styleUrl: './celebrations-widget.component.scss',
    providers: [CelebrationsWidgetStore],
    imports: [
        TableModule,
        TranslatePipe,
        NgClass,
        TableComponent,
        PrimeTemplate,
        EmptyStateComponent,
    ],
})
export class CelebrationsWidgetComponent {
    protected readonly store = inject(CelebrationsWidgetStore);

    items = input<{ serviceId: string, widget: UserWidgetSetting }[]>([]);

    protected data = signal<FolderGridItemObject[]>([]);

    protected prefix = 'dashboard.celebrations.';

    protected todayBirthdays = signal<any[]>([]);
    protected todayBirthdaysColumns = computed(() => {
        const showServiceInfo = [...new Set(this.items().map(x => x.serviceId))].length > 1;

        let cols = [
            {
                name: 'icon',
            },
            clientChipField('client', 'person', false),
            {
                name: 'age',
                dataKey: null,
                data: (item: FolderGridItemObject) => dateifyOrNow(null).getFullYear() - dateifyOrNow(item.person.birthdate).getFullYear(),
            },
        ];

        if (showServiceInfo) {
            cols = [
                ...cols.slice(0, 1),
                serviceField('service', 'serviceId'),
                ...cols.slice(1),
            ];
        }

        return cols;
    });

    protected todayNameDays = signal<FolderGridItemObject[]>([]);
    protected todayNameDaysColumns = computed(() => {
        const showServiceInfo = [...new Set(this.items().map(x => x.serviceId))].length > 1;

        const cols = [
            {
                name: 'icon',
            },
        ];

        if (showServiceInfo) {
            cols.push(
                serviceField('service', 'serviceId'),
            );
        }

        cols.push(
            clientChipField('client', 'person', false),
        );

        return cols;
    });

    protected upcoming = signal<any[]>([]);
    protected upcomingColumns = computed(() => {
        const showServiceInfo = [...new Set(this.items().map(x => x.serviceId))].length > 1;

        let cols = [
            {
                name: 'icon',
                dataKey: 'type',
            },
            dayWithRelationsField('day', 'celebrationDate'),
            clientChipField('client', 'person', false),
            {
                name: 'age',
                dataKey: null,
                data: (item: FolderGridItemObject) => dateifyOrNow(null).getFullYear() - dateifyOrNow(item.person.birthdate).getFullYear(),
            },
            {
                name: 'type',
            },
        ];

        if (showServiceInfo) {
            cols = [
                ...cols.slice(0, 1),
                serviceField('service', 'serviceId'),
                ...cols.slice(1),
            ];
        }

        return cols;
    });

    constructor() {
        effect(async () => {
            if (!this.items().length) {
                return;
            }

            const nameDays: NameDayObject[] = (await this.store.api.getListNameDays({
                filter: [
                    [{
                        column: 'name',
                        operator: 'NEMPTY',
                        value: ['true'],
                    }],
                ],
                paging: { page: 1, itemsPerPage: 500 },
            }))?.['items'] || [];

            const todayDayNames = nameDays.find(x => {
                const today = new Date();

                return x.month === (today.getMonth() + 1) && x.day === today.getDate();
            })?.name.split(',') || [];

            const promisesFolders = this.items().map(async (item) => {
                const serviceNameDays = nameDays.filter(x => this.generateNextDays(new Date(), item.widget.days).includes(x.date));
                const names = serviceNameDays.flatMap((x: NameDayObject) => x.name.split(','));

                return [
                    await this.store.api.getFolders({
                        filter: [
                            [{
                                column: 'serviceId',
                                operator: 'EQ',
                                value: [item.serviceId],
                            }],
                            [{
                                column: 'status',
                                operator: 'IN',
                                value: [FolderStatusEnum.IN_CARE, FolderStatusEnum.WITH_VOUCHER],
                            }],
                            [{
                                column: 'birthdateCelebration',
                                operator: 'EQ',
                                value: [item.widget.days.toString()],
                            }],
                        ],
                    }, folderCelebrationsFrag),
                    await this.store.api.getFolders({
                        filter: [
                            [{
                                column: 'serviceId',
                                operator: 'EQ',
                                value: [item.serviceId],
                            }],
                            [{
                                column: 'status',
                                operator: 'IN',
                                value: [FolderStatusEnum.IN_CARE, FolderStatusEnum.WITH_VOUCHER],
                            }],
                            [{
                                column: 'name',
                                operator: 'IN',
                                value: names,
                            }],
                        ],
                    }, folderCelebrationsFrag),
                ];
            });

            const folders = await Promise.all(promisesFolders);

            const birthdaysItems = folders.flatMap(obj => obj[0].items as FolderGridItemObject[]);
            const nameDayItems = folders.flatMap(obj => obj[1].items as FolderGridItemObject[]);

            // today birthdays data - ordered by birthdate from higher to lower
            const todayBirths = birthdaysItems.filter((x: FolderGridItemObject) => {
                const bDatePartials = formatDate(dateifyOrNow(x.person.birthdate), 'MM-dd', 'en-US');
                const nowPartials = formatDate(new Date(), 'MM-dd', 'en-US');
                return bDatePartials === nowPartials;
            });

            // @ts-ignore
            this.todayBirthdays.set(todayBirths.sort((a, b) => dateifyOrNow(a.person.birthdate) - dateifyOrNow(b.person.birthdate)));

            // today name days data
            const todayNamedays = nameDayItems.filter((x: FolderGridItemObject) => todayDayNames.includes(x.person.name));
            this.todayNameDays.set(todayNamedays);

            // upcoming data
            const upBirthdaysItems = birthdaysItems.filter(x => !todayBirths.map(y => y.id).includes(x.id)).map(item => {
                const age = dateifyOrNow(null).getFullYear() - dateifyOrNow(item.person.birthdate).getFullYear();

                return <UpcomingItem>{
                    ...item,
                    celebrationDate: this.calcCelebrationDate(item.person.birthdate),
                    type: age % 10 === 0 ? 'jubilee' : age % 5 === 0 ? 'semiJubilee' : 'birthday',
                };
            });
            const upNameDayItems = nameDayItems.filter(x => !todayNamedays.map(y => y.id).includes(x.id)).map(item => {
                const celebrationDatePartial = nameDays.find(x => x.name.split(',').map(y => y.toLowerCase()).includes(item.person.name.toLowerCase()));

                return <UpcomingItem>{
                    ...item,
                    celebrationDate: celebrationDatePartial ? new Date((new Date()).getFullYear(), celebrationDatePartial.month - 1, celebrationDatePartial.day) : null,
                    type: 'nameDay',
                };
            });

            const upcoming: UpcomingItem[] = [...upBirthdaysItems, ...upNameDayItems].sort((a, b) => {
                if (!a.celebrationDate && !b.celebrationDate) {
                    return a.serviceId - b.serviceId;
                }
                if (!a.celebrationDate) return 1;
                if (!b.celebrationDate) return -1;

                // @ts-ignore
                const dateComparison = a.celebrationDate - b.celebrationDate;
                if (dateComparison !== 0) {
                    return dateComparison;
                }
                return a.serviceId - b.serviceId;
            });

            this.upcoming.set(upcoming);
        });
    }

    private calcCelebrationDate(date: string | null): Date | null {
        if (!date) return null;

        const today = new Date();
        const birthday = new Date(date);

        return new Date(today.getFullYear(), birthday.getMonth(), birthday.getDate());
    }

    private generateNextDays(startDate: Date, daysCount: number): string[] {
        const result: string[] = [];

        for (let i = 0; i < daysCount; i++) {
            const currentDate = new Date(startDate);
            currentDate.setDate(startDate.getDate() + i);

            const month = String(currentDate.getMonth() + 1).padStart(2, '0');
            const day = String(currentDate.getDate()).padStart(2, '0');

            result.push(`${month}-${day}`);
        }

        return result;
    }
}
